import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import { NotFound } from "@app/shared/ErrorPages";
import { ResourceType } from "@app/resources";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { ReportCustomization } from "@app/report-customization/build-reports/ReportCustomization";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { NetworkIntegrityAdequacyImpactGapPage } from "@app/projects/network-assessment/network-integrity";
import { NetworkIntegrityContextProvider } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { CloneProject } from "@app/projects/clone/CloneProject";
import { CreateProjectWizard } from "@app/projects/create";
import { ProjectEditRouter } from "@app/projects/edit";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import ResourceService from "@app/resources/resourceService";
import { ExportData } from "@app/exports/ExportData";
import BulkProjectEdit from "@app/projects/edit/BulkProjectEdit";
import CountyProjectOverview from "@app/projects/network-assessment/CountyProjectOverview";
import { ExcessWarningsBreakdownPage } from "@app/projects/network-assessment/network-integrity/ExcessWarningsBreakdownPage";
import { NetworkIntegrityViewAllPage } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityViewAllPage";
import { NetworkIntegrityReportsPage } from "@app/projects/network-assessment/network-integrity/exports";
import { NetworkIntegrityProvidersPage } from "@app/projects/network-assessment/network-integrity/providers/NetworkIntegrityProvidersPage";
import { MultiMapExportContextProvider, MultiMapExportPage } from "@app/projects/network-assessment/multi-map-export";
import AdvancedExclusionsWizard from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsWizard";
import ZipProjectOverview from "@app/projects/zip-projects/ZipProjectOverview";

type ProjectsRouterProps = RouteComponentProps<undefined, undefined, ExportDataRouteState>;

/** Renders the routing components for the projects sub-section of the app. */
export default function ProjectsRouter(props: ProjectsRouterProps): JSX.Element {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const user = useContext(UserContext);

  return (
    <Switch>
      <Route path={`${props.match.path}/create`} component={CreateProjectWizard} />
      <Route path={`${props.match.path}/:projectId/clone`} component={CloneProject} />
      <Route path={`${props.match.path}/:projectId/edit`} component={ProjectEditRouter} />
      {featureFlags.has(Features.LOB_EXCLUSIONS) && user.roles.includes(Roles.PROVIDER_EXCLUDE_USER) && (<Route path={`${props.match.path}/:projectId/advanced-exclusions`} component={AdvancedExclusionsWizard} />)}
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/all/:aggregateGroup`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><NetworkIntegrityViewAllPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/providers/:providerType/:aggregateGroup/:aggregateType`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><NetworkIntegrityProvidersPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/reports/:providerType/:aggregateGroup/:aggregateType`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><NetworkIntegrityReportsPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/reports/:providerType/:aggregateGroup`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><NetworkIntegrityReportsPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/excess-warnings-breakdown/:providerType/:aggregateGroup`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><ExcessWarningsBreakdownPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/adequacy-impact-report/:providerType/:impactType`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><NetworkIntegrityReportsPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/network-integrity/adequacy-impact/:providerType/:impactType`}>
          <ErrorBoundary user={user}><NetworkIntegrityContextProvider><NetworkIntegrityAdequacyImpactGapPage /></NetworkIntegrityContextProvider></ErrorBoundary>
        </Route>
        <Route path={`${props.match.path}/:projectId/:projectRunConfigurationId/map-exporter`}>
          <ErrorBoundary user={user}><MultiMapExportContextProvider><MultiMapExportPage /></MultiMapExportContextProvider></ErrorBoundary>
        </Route>

      {props.location.pathname.includes("/export") && !props.location.state &&
        <Redirect from={`${props.match.path}/:projectId/:module/export/:runConfigurationId/:isLatestPublished`} to={`${props.match.path}/:projectId`} />}

      {/* new create report page */}
      {/* Unfortunately props.match.params isn't populated until after the route is chosen, so we have to rely on the pathname */}
      {
        featureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) &&
        props.location.pathname.includes("adequacy") && (
          <Route path={`${props.match.path}/:projectId/:module/export/:runConfigurationId/:isLatestPublished`} component={ExportsProjectTypeLoader} />
        )
      }

      {/* old create report page */}
      <Route exact={true} path={`${props.match.path}/:projectId/:module/export/:runConfigurationId/:isLatestPublished`} component={ExportData} />
      {props.location.pathname.includes("/export") && !props.location.state &&
        <Redirect
          from={`${props.match.path}/:projectId/:module/:runConfigurationId/:scope/:networkType/:resultType/:key/export`}
          to={`${props.match.path}/:projectId/:module/:runConfigurationId/:scope/:networkType/:resultType/:key`}
        />
      }
      <Route path={`${props.match.path}/:projectId/:module/:runConfigurationId/:scope/:networkType/:resultType/:key/export`} component={ExportData} />

      <Redirect push={false} strict={true} exact={true} from={`${props.match.path}/:projectId/`} to={{ pathname: `${props.match.path}/:projectId`, state: props.location.state }} />
      <Route path={`${props.match.path}/edit`} component={BulkProjectEdit} />
      {/* trailing a parameter with ? makes it optional */}
      <Route path={`${props.match.path}/:projectId/:module?`} component={ProjectTypeLoader} /> {/*This needs to be the last route*/}
      <Route component={NotFound} />
    </Switch>
  );
}

const ProjectTypeLoader: FC<RouteComponentProps<{ projectId: string }>> = (props: RouteComponentProps<{ projectId: string }>) => {
  const [resourceType, setResourceType] = useState<ResourceType>();
  const resourceService = useRef(new ResourceService()).current;
  const params: { projectId: string } = props.match.params as { projectId: string };

  useEffect(() => {
    resourceService.getResourceType(parseInt(params.projectId, 10))
      .then((response) => {
        setResourceType(response);
      });
  }, [params.projectId]);

  switch (resourceType) {
    case undefined:
      return (
        <div className="mt-3 ms-3">
          <LoadingSpinner />
        </div>
      );
    case ResourceType.CustomProject:
      return <CountyProjectOverview {...props} />;
    case ResourceType.ZipAnalysisProject:
      return <ZipProjectOverview />;
  }
}

// this function should be temporary, until zip projects support report customization without a feature flag
/* oxlint-disable @typescript-eslint/no-explicit-any */
const ExportsProjectTypeLoader: FC<RouteComponentProps<any, undefined, unknown>> = (props: RouteComponentProps<any, undefined, unknown>) => {
  const [resourceType, setResourceType] = useState<ResourceType>();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const resourceService = useRef(new ResourceService()).current;
  const params: { projectId: string } = props.match.params as { projectId: string };

  if (enabledFeatureFlags.has(Features.ZIP_REPORT_CONFIGURATION)) {
    return <ReportCustomization />;
  }

  useEffect(() => {
    resourceService.getResourceType(parseInt(params.projectId, 10))
      .then((response) => {
        setResourceType(response);
      });
  }, [params.projectId]);

  switch (resourceType) {
    case undefined:
      return (
        <div className="mt-3 ms-3">
          <LoadingSpinner />
        </div>
      );
    case ResourceType.CustomProject:
      return <ReportCustomization />;
    case ResourceType.ZipAnalysisProject:
      return <ExportData {...props} />;
  }
}